import { ChangeEvent, useCallback, useState } from 'react';
import { blueButtonClass, BRDiv } from 'src/components/common/common';
import { InputForm } from 'src/components/common/form/input/InputForm';
import { SingleDateForm } from 'src/components/common/form/SingleDateForm';
import ModalWrapper from 'src/components/common/modal/ModalWrapper';
import { useCommonCodeStore } from 'src/store/useCommonCodeStore';
import useUiuxStore from 'src/store/useUiuxStore';
import { formatDate, toLocalDateTime } from 'src/utils/string/stringUtil';
import { usePRD3020_R01Store } from './Store_PRD3020_R01';
import { defaultPRD3020_R01Dto, PRD3020_R01Dto } from './Dto_PRD3020_R01';
import { DropDownForm } from 'src/components/common/form/DropdownForm';

interface Props {
	handleSaveClick: () => void;
}

export const Modal_PRD3020_R01 = ({
    handleSaveClick,
}: Props) => {
    const { 
		editPRD3020_R01, setEditPRD3020_R01,
		modalVisible, setModalVisible
	} = usePRD3020_R01Store();
	const { downtimeValueList } = useCommonCodeStore();
	const { setCodeSearchModalProps, setAddressModalProps } = useUiuxStore();

	
	const [workStartDtPickerVisible, setWorkStartDtPickerVisible] = useState(false);
	const [workStartDtPickerPos, setWorkStartDtPickerPos] = useState<{top: number, left: number, height: number}>({top: 0, left: 0, height: 0});
	const [workEndDtPickerVisible, setWorkEndDtPickerVisible] = useState(false);
	const [workEndDtPickerPos, setWorkEndDtPickerPos] = useState<{top: number, left: number, height: number}>({top: 0, left: 0, height: 0});
	
	const handleDateChange = useCallback((field: keyof PRD3020_R01Dto, value: any) => {

		//시작시간과 종료시간 중 어느곳에서 변경이 있는지 확인
		if(field === 'downtimeStartDt') {
			const workStartTime = new Date(editPRD3020_R01?.downtimeStartDt || new Date());
			
			if (typeof value === 'string' && value.includes('-')) {
				const [y, m, d] = value.split('-').map(Number);
				workStartTime.setFullYear(y, m - 1, d);
			}

			// 시간 설정
			workStartTime.setHours(editPRD3020_R01?.downtimeStartHour || 0);
			workStartTime.setMinutes(editPRD3020_R01?.downtimeStartMinute || 0);
			workStartTime.setSeconds(0);

			setEditPRD3020_R01({
				...defaultPRD3020_R01Dto,
				...editPRD3020_R01,
				downtimeStartTime: toLocalDateTime(workStartTime),
				downtimeStartDt: formatDate(workStartTime)
			});

		} else if(field === 'downtimeEndDt') {
			const workEndTime = new Date(editPRD3020_R01?.downtimeEndDt || new Date());
			
			if (typeof value === 'string' && value.includes('-')) {
				const [y, m, d] = value.split('-').map(Number);
				workEndTime.setFullYear(y, m - 1, d);
			}

			// 시간 설정
			workEndTime.setHours(editPRD3020_R01?.downtimeEndHour || 0);
			workEndTime.setMinutes(editPRD3020_R01?.downtimeEndMinute || 0);
			workEndTime.setSeconds(0);

			setEditPRD3020_R01({
				...defaultPRD3020_R01Dto,
				...editPRD3020_R01,
				downtimeEndTime: toLocalDateTime(workEndTime),
				downtimeEndDt: formatDate(workEndTime)
			});
		}
	}, [editPRD3020_R01]);

	const handleChange = (e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLSelectElement>) => {
		const { name, value } = e.target;
        const numValue = Number(value);

		if(name === 'downtimeStartHour' || name === 'downtimeStartMinute') {
			// 기존 날짜 기준 (시간 계산용)
			const downtimeStartTime = new Date(editPRD3020_R01?.downtimeStartDt || new Date());
			// 화면 표시용 날짜 유지 (시간이 24를 넘어도 날짜가 바뀌지 않도록 원본 유지)
			const originalStartDt = new Date(editPRD3020_R01?.downtimeStartDt || new Date());
            
            let newHour = editPRD3020_R01?.downtimeStartHour || 0;
            let newMinute = editPRD3020_R01?.downtimeStartMinute || 0;

            if (name === 'downtimeStartHour') newHour = numValue;
            if (name === 'downtimeStartMinute') newMinute = numValue;

            // Date 객체 업데이트 (24시간 넘으면 날짜 넘어감)
			downtimeStartTime.setHours(newHour, newMinute, 0);

			setEditPRD3020_R01({
				...defaultPRD3020_R01Dto,
				...editPRD3020_R01,
				downtimeStartTime: toLocalDateTime(downtimeStartTime),
				downtimeStartDt: formatDate(originalStartDt), // [수정] 시간이 24를 넘어도 화면상 날짜는 유지
                downtimeStartHour: newHour,
                downtimeStartMinute: newMinute
			});
		}
		else if(name === 'downtimeEndHour' || name === 'downtimeEndMinute') {
			// 기존 날짜 기준
			const downtimeEndTime = new Date(editPRD3020_R01?.downtimeEndDt || new Date());
			// 화면 표시용 날짜 유지
			const originalEndDt = new Date(editPRD3020_R01?.downtimeEndDt || new Date());
            
            let newHour = editPRD3020_R01?.downtimeEndHour || 0;
            let newMinute = editPRD3020_R01?.downtimeEndMinute || 0;

            if (name === 'downtimeEndHour') newHour = numValue;
            if (name === 'downtimeEndMinute') newMinute = numValue;
			
			// Date 객체 업데이트
			downtimeEndTime.setHours(newHour, newMinute, 0);
			
			setEditPRD3020_R01({
				...defaultPRD3020_R01Dto,
				...editPRD3020_R01,
				downtimeEndTime: toLocalDateTime(downtimeEndTime),
				downtimeEndDt: formatDate(originalEndDt), // [수정] 시간이 24를 넘어도 화면상 날짜는 유지
                downtimeEndHour: newHour,
                downtimeEndMinute: newMinute
			});
		} else {
            // 그 외 일반 필드 처리
            setEditPRD3020_R01({
                ...defaultPRD3020_R01Dto,
                ...editPRD3020_R01,
                [name]: value
            });
        }
	}

	const wrapperOption = {
		wrapperStyle: "w-[50px] min-w-[50px] mb-[4px]",
		titleStyle: "w-[150px] min-w-[150px] text-left",
		inputStyle: "w-[50px] min-w-[50px] ",
	}

	
    // 3. 모든 Hook 호출이 끝난 후 조건부 리턴을 수행합니다.
    // if (!modalVisible) return null;

    return (
        <ModalWrapper
            size={'sm'}
            width={500}
            overlay={true}
            onOverlayClick={() => setModalVisible(!modalVisible)}
            onEscKeyDown={() => {
                setModalVisible(!modalVisible);
            }}
        >
            <div className="w-full h-[20%] flex items-center px-[24px] bg-gray-100">
                <p className={`font-bold `}>
                    비가동시간 등록
                </p>
            </div>
			
			
			<div className="h-[60%] flex flex-wrap content-start pt-[12px] pl-[12px] pr-[12px] overflow-y-auto">
				<BRDiv />
				<SingleDateForm
					title="시작시간"
					value={editPRD3020_R01?.downtimeStartDt ? new Date(editPRD3020_R01.downtimeStartDt) : undefined}
					setValue={(value: Date | undefined) => handleDateChange('downtimeStartDt', formatDate(value))}
					editable={true}
					visible={workStartDtPickerVisible} setVisible={setWorkStartDtPickerVisible}
					onChange={handleChange}
					pickerPos={workStartDtPickerPos} setPickerPos={setWorkStartDtPickerPos}
				/>
				
					<InputForm 
						title="시작시간" 
						value={editPRD3020_R01?.downtimeStartHour || 0} 
						editable={true}
						name="downtimeStartHour" 
						wrapperOption={wrapperOption}
						onChange={handleChange} 
						titleless={true}
						dragSelectAll={true}
					/>
					<span className={` text-center`}>시</span>
					<InputForm 
						title="시작시간" 
						value={editPRD3020_R01?.downtimeStartMinute || 0} 
						editable={true}
						name="downtimeStartMinute" 
						wrapperOption={wrapperOption}
						onChange={handleChange} 
						titleless={true}
						dragSelectAll={true}
					/>
					<span className={` text-center`}>분</span>
				<BRDiv />
				<SingleDateForm
					title="종료시간"
					value={editPRD3020_R01?.downtimeEndDt ? new Date(editPRD3020_R01.downtimeEndDt) : undefined}
					setValue={(value: Date | undefined) => handleDateChange('downtimeEndDt', formatDate(value))}
					editable={true}
					visible={workEndDtPickerVisible} setVisible={setWorkEndDtPickerVisible}
					onChange={handleChange}
					pickerPos={workEndDtPickerPos} setPickerPos={setWorkEndDtPickerPos}
				/>
				
					<InputForm 
						title="종료시간" 
						value={editPRD3020_R01?.downtimeEndHour || 0} 
						editable={true}
						name="downtimeEndHour" 
						wrapperOption={wrapperOption}
						onChange={handleChange} 
						titleless={true}
						dragSelectAll={true}
					/>
					<span className={` text-center`}>시</span>
					<InputForm 
						title="종료시간" 
						value={editPRD3020_R01?.downtimeEndMinute || 0} // [수정] downtimeStartMinute -> downtimeEndMinute
						editable={true}
						name="downtimeEndMinute" 
						wrapperOption={wrapperOption}
						onChange={handleChange} 
						titleless={true}
						dragSelectAll={true}
					/>
					<span className={` text-center`}>분</span>
				<BRDiv />
				<DropDownForm
					title="비가동사유"
					name="downtimeCase"
					initLabelLess={true}
					value={editPRD3020_R01?.downtimeCase}
					data={downtimeValueList}
					editable={true}
					onChange={handleChange}
				/>
				<BRDiv />
			</div>
            <div className="w-full h-[20%] flex items-center justify-end px-[24px] gap-[8px]">
                <button
                    className={`${blueButtonClass} flex-shrink-0 text-white font-semibold px-4  rounded shadow h-[30px]`}
                    onClick={handleSaveClick}
                    disabled={false}
                >
                    확인
                </button>
                <button
                    className={`${blueButtonClass} flex-shrink-0 text-white font-semibold px-4  rounded shadow h-[30px]`}
                    onClick={() => setModalVisible(!modalVisible)}
                    disabled={false}
                >
                    취소
                </button>
            </div>
        </ModalWrapper>
    );
};

